import { vorgangNoteService, type AggregatedNoteDto } from "../../../services/vorgangNoteService";

/**
 * Die Notizblöcke, die beim Anlegen des ERSTEN To-dos mitwandern.
 *
 * ══ WARUM ES DIESEN SCHRITT BRAUCHT ════════════════════════════════════════
 * Design-Stand 08.09.2026: „Sobald das erste To-do entsteht, wird die
 * bisherige Vorgangsnotiz zur Notiz des ersten To-dos."
 *
 * Das ist nicht nur Kosmetik. `baueVerlauf` liefert seit dem 08.09.2026 keine
 * Notizen mehr — das Umhängen der Verlaufseinträge (Mail, Anruf, Dokument)
 * erfasst sie also NICHT. Und `ArbeitsstrangV4` rendert den Vorgangsstrom nur
 * unter `{!hatTodos && …}`. Bliebe ein Notizblock am Vorgang hängen, wäre er
 * ab dem ersten To-do nirgends mehr zu sehen — nicht gelöscht, aber
 * unerreichbar.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Gemeint sind nur die EIGENEN Blöcke des Vorgangs: `source === "vorgang"`
 * (eine `VorgangNote`) ohne `todoId`. Bei `source === "todo"` ist `todoId` der
 * Besitzer, bei `source === "vorgang"` ein `#`-Verweis — ein gesetzter Wert
 * heißt in beiden Fällen: der Block steht schon bei einem To-do.
 */
export function notizenAmVorgang(notizen: AggregatedNoteDto[]): AggregatedNoteDto[] {
    // Die LAUFENDE NOTIZ (User 01.11.2026) bleibt außen vor: Sie gehört
    // absichtlich dem Vorgang, wandert nicht ins erste To-do und macht den
    // Vorgang nicht zur „ersten To-do-Karte".
    return notizen.filter(
        (n) => (n.source ?? "vorgang") === "vorgang" && (n.todoId ?? null) === null && n.laufend !== true,
    );
}

/** Die Zeilen der laufenden Notiz des Vorgangs — Blöcke und ihre Kommentare. */
export function laufendeNotiz<T extends AggregatedNoteDto>(notizen: T[]): T[] {
    return notizen.filter((n) => n.laufend === true && (n.source ?? "vorgang") === "vorgang" && (n.todoId ?? null) === null);
}

/** Alles außer der laufenden Notiz — für die Stränge, die sie nicht zeigen. */
export function ohneLaufendeNotiz<T extends AggregatedNoteDto>(notizen: T[]): T[] {
    return notizen.some((n) => n.laufend === true) ? notizen.filter((n) => n.laufend !== true) : notizen;
}

/**
 * Ein Block wechselt an ein anderes To-do — beim Anlegen des ersten
 * automatisch, per Rechtsklick am Block von Hand (PO 13./14.09.2026).
 * `todoId = null` schickt ihn zurück an den Vorgang.
 *
 * ══ NUR `todoId` — SONST NICHTS ════════════════════════════════════════════
 * Das Backend unterscheidet zwei Dinge (`vorgang-note.service.ts`): den
 * INHALT einer Notiz ändert nur, wer sie geschrieben hat; das reine UMHÄNGEN
 * an ein To-do darf jeder, der den Vorgang bearbeiten darf. Erkannt wird der
 * zweite Fall daran, dass die Eingabe AUSSCHLIESSLICH `todoId` trägt
 * (`istNurUmhaengenAnTodo`).
 *
 * Käme hier auch nur ein weiteres Feld mit — `sortIndex`, `text`, `refs` —,
 * wäre es wieder eine Bearbeitung, und jede Notiz einer Kollegin liefe in
 * einen 403. Sie bliebe dann mit `todoId = null` am Vorgang hängen und wäre
 * in V4 nirgends mehr zu sehen. Deshalb steht der Aufruf hier und nicht als
 * Lambda in der Seite: so hat die Regel einen Ort und einen Test.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function umhaengeNotizAnTodo(noteId: number, todoId: number | null): Promise<unknown> {
    return vorgangNoteService.update(noteId, { todoId });
}

export interface NotizUebernahme {
    /** Die Blöcke, die tatsächlich beim ersten To-do gelandet sind. */
    uebernommen: AggregatedNoteDto[];
    /** Blöcke, deren Umhängen fehlschlug — sie hängen weiter am Vorgang. */
    fehlgeschlagen: AggregatedNoteDto[];
}

/**
 * Hängt die Vorgangs-Notizblöcke an das erste To-do.
 *
 * BRICHT NICHT AB, wenn ein einzelner Block scheitert: der Rest soll trotzdem
 * ankommen, und das bereits angelegte To-do bleibt ohnehin bestehen (so hält
 * es der übrige Anlage-Pfad auch beim Zielereignis). Was fehlschlug, kommt
 * zurück — der Aufrufer MUSS es dem Nutzer sagen, sonst verschwindet eine
 * Notiz still aus der Ansicht.
 *
 * Nacheinander statt `Promise.all`: dieselbe Reihenfolge wie beim Umhängen der
 * Verlaufseinträge, und ein Fehler bleibt einem Block zuzuordnen.
 */
export async function uebernimmNotizen(
    notizen: AggregatedNoteDto[],
    todoId: number,
    umhaengen: (noteId: number, todoId: number) => Promise<unknown>,
): Promise<NotizUebernahme> {
    const uebernommen: AggregatedNoteDto[] = [];
    const fehlgeschlagen: AggregatedNoteDto[] = [];
    for (const n of notizenAmVorgang(notizen)) {
        try {
            await umhaengen(n.id, todoId);
            uebernommen.push(n);
        } catch (err) {
            console.error("[VorgangDetailV4] Notizblock übernehmen fehlgeschlagen", n.id, err);
            fehlgeschlagen.push(n);
        }
    }
    return { uebernommen, fehlgeschlagen };
}

/**
 * Der Satz, den der Nutzer zu sehen bekommt, wenn Blöcke liegen geblieben
 * sind — `null`, wenn alles angekommen ist.
 *
 * Klartext ohne Technik: die Ansicht zeigt den betroffenen Text ab jetzt
 * nirgends mehr, und genau das muss dastehen. Ein stilles `console.error`
 * reichte nicht — niemand sieht es.
 */
export function notizUebernahmeWarnung(fehlgeschlagen: AggregatedNoteDto[]): string | null {
    if (fehlgeschlagen.length === 0) return null;
    const wieViele =
        fehlgeschlagen.length === 1 ? "Eine Notiz" : `${fehlgeschlagen.length} Notizen`;
    return (
        `${wieViele} des Vorgangs konnte(n) nicht in das erste To-do übernommen werden ` +
        "und ist/sind deshalb in dieser Ansicht nicht mehr zu sehen. " +
        "Bitte die Seite neu laden und es erneut versuchen."
    );
}
